import React, { useEffect, useRef, useState } from 'react';

/**
 * In-app 3D preview — loads the exact same KMZ any page's "KMZ (3D)" button
 * would download, and renders it in an embedded Cesium globe instead of
 * requiring Google Earth. Reuses 100% of the geometry already built for
 * export (BRA cones, OLS/ADG surfaces, obstacle hull, etc.) — this is only
 * a viewer, no new geometry logic.
 *
 * No Cesium Ion account is configured for this app, so the base imagery is
 * Cesium's own offline-bundled Natural Earth II texture (no token needed)
 * and terrain is the default flat WGS84 ellipsoid — accurate for our
 * purposes since every surface already carries true AMSL altitude per
 * vertex. Swap in Cesium.createWorldImageryAsync()/createWorldTerrainAsync()
 * later if an Ion token is ever added, for satellite imagery + real terrain.
 */
export default function Cesium3DPreviewModal({ title, kmlUrl, fetchKmz, onClose }) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const Cesium = await import('cesium');
                if (cancelled || !containerRef.current) return;

                const baseLayer = new Cesium.ImageryLayer(
                    new Cesium.TileMapServiceImageryProvider({
                        url: Cesium.buildModuleUrl('Assets/Textures/NaturalEarthII'),
                    })
                );

                const viewer = new Cesium.Viewer(containerRef.current, {
                    baseLayer,
                    baseLayerPicker: false,
                    geocoder: false,
                    homeButton: true,
                    sceneModePicker: true,
                    navigationHelpButton: false,
                    animation: false,
                    timeline: false,
                    fullscreenButton: true,
                    infoBox: true,
                    selectionIndicator: true,
                });
                viewerRef.current = viewer;

                // fetchKmz (a page-supplied POST, when the export needs a
                // body) takes priority; otherwise a plain GET to kmlUrl.
                let blob;
                if (fetchKmz) {
                    blob = await fetchKmz();
                } else {
                    const resp = await fetch(kmlUrl);
                    if (!resp.ok) {
                        const text = await resp.text().catch(() => '');
                        throw new Error(text || `Server responded ${resp.status}`);
                    }
                    blob = await resp.blob();
                }
                if (cancelled) return;

                const dataSource = await Cesium.KmlDataSource.load(blob, {
                    camera: viewer.scene.camera,
                    canvas: viewer.scene.canvas,
                    clampToGround: false, // respect each placemark's own altitudeMode
                });
                if (cancelled) return;

                await viewer.dataSources.add(dataSource);
                if (cancelled) return;
                await viewer.flyTo(dataSource);
                if (!cancelled) setStatus('ready');
            } catch (e) {
                console.error('Cesium 3D preview error:', e);
                if (!cancelled) {
                    setError(e.message || String(e));
                    setStatus('error');
                }
            }
        })();

        return () => {
            cancelled = true;
            if (viewerRef.current) {
                viewerRef.current.destroy();
                viewerRef.current = null;
            }
        };
        // Runs once per mount — this modal is only ever mounted fresh when
        // opened and unmounted on close, so kmlUrl/fetchKmz never change
        // mid-lifecycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 6000,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                style={{
                    width: '92vw', height: '88vh', maxWidth: 1400,
                    background: '#07111e', borderRadius: 12, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    border: '1px solid rgba(255,255,255,0.12)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                }}
            >
                <div
                    style={{
                        padding: '10px 16px', display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.12)',
                        flexShrink: 0,
                    }}
                >
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>🌐</span> {title || '3D Preview'}
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                            color: '#ccc', borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                            fontWeight: 700, fontSize: 11,
                        }}
                    >
                        ✕ Close
                    </button>
                </div>
                <div style={{ flex: 1, position: 'relative' }}>
                    <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
                    {status === 'loading' && (
                        <div
                            style={{
                                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', color: '#9db4c9', fontSize: 13,
                                background: 'rgba(7,17,30,0.85)', pointerEvents: 'none',
                            }}
                        >
                            Loading 3D surfaces…
                        </div>
                    )}
                    {status === 'error' && (
                        <div
                            style={{
                                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', color: '#e84040', fontSize: 13,
                                background: 'rgba(7,17,30,0.92)', padding: 20, textAlign: 'center',
                            }}
                        >
                            Failed to load 3D preview: {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
